'use memo'

import {
  Condition,
  DepositPayable,
  FormCondition,
  LinkedDateImpact,
  RenderedClauseText,
  StaffPreviewLinkedDatesRequestSourceDateType,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxDivider,
  AgxOnOrDaysFrom,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTypography,
  ContractDateType,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import { useEffect, useState } from 'react'
import { LinkedDateWarningModal } from '../../modals/LinkedDateWarningModal'
import ConditionDate from '../../components/ConditionDate'
import { useFormConditionDueDate } from '../../components/useFormConditionDueDate'

interface SpecialConditionClauseProps {
  clause: RenderedClauseText
  conditionId: string
  contractId: string
  campaignId: string
  isLastClause: boolean
  deferDueDateSave?: boolean
  dueDateCondition: FormCondition | undefined
  specialConditions: SpecialConditionData[]
  specialConditionIdToClauseNumberMap: {
    [key: string]: string
  }
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => void | Promise<void>
}

const conditionsToInclude: Condition[] = [
  Condition.OnDate,
  Condition.NdaysFromDate,
  Condition.OnSpecificDate,
  Condition.AsPerContract,
]

const getInitialDueDateCondition = (
  dueDateCondition: FormCondition | undefined,
  clauseDueDate: string | undefined
): FormCondition => {
  if (dueDateCondition?.condition) {
    return {
      ...dueDateCondition,
      depositPayable:
        dueDateCondition.depositPayable ??
        (dueDateCondition.condition === Condition.OnSpecificDate
          ? DepositPayable.On
          : DepositPayable.NDaysFrom),
    }
  }

  if (dueDateCondition?.referenceDate) {
    return {
      ...dueDateCondition,
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
    }
  }

  if (clauseDueDate) {
    return {
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
      referenceDate: clauseDueDate,
    }
  }

  return {}
}

const SpecialConditionClause = ({
  clause,
  conditionId,
  contractId,
  campaignId,
  isLastClause,
  dueDateCondition,
  specialConditions,
  specialConditionIdToClauseNumberMap,
  onDueDateUpdated,
}: SpecialConditionClauseProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [saveState, setSaveState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const [validateDueDate, setValidateDueDate] = useState(false)
  const [isDueDateValid, setIsDueDateValid] = useState(false)
  const [linkedDateImpacts, setLinkedDateImpacts] = useState<
    LinkedDateImpact[]
  >([])

  const saveButtonText =
    saveState === SAVE_BUTTON_STATES.SAVED
      ? 'Changes Saved'
      : saveState === SAVE_BUTTON_STATES.UNSAVED
        ? 'Save'
        : saveState === SAVE_BUTTON_STATES.ERROR
          ? 'Error'
          : saveState === SAVE_BUTTON_STATES.SAVING
            ? 'Saving...'
            : 'Save Changes'

  useEffect(() => {
    if (!isEditing) setSaveState(SAVE_BUTTON_STATES.UNSAVED)
  }, [isEditing])

  const [localDueDateCondition, setLocalDueDateCondition] =
    useState<FormCondition>(() =>
      getInitialDueDateCondition(dueDateCondition, clause.dueDate)
    )
  const calculatedDueDate = useFormConditionDueDate(localDueDateCondition)

  useEffect(
    () =>
      setLocalDueDateCondition(
        getInitialDueDateCondition(dueDateCondition, clause.dueDate)
      ),
    [dueDateCondition, clause.dueDate]
  )

  const invalidateUseStaffGetContractDates =
    useInvalidateUseStaffGetContractDates()

  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {
      setSaveState(SAVE_BUTTON_STATES.ERROR)
    },
  })

  const saveDueDate = async () => {
    try {
      await onDueDateUpdated(
        conditionId,
        clause.clauseId ?? '',
        localDueDateCondition.condition ? localDueDateCondition : undefined,
        calculatedDueDate.dueDateString || undefined
      )
      setSaveState(SAVE_BUTTON_STATES.SAVED)
      setIsEditing(false)
      await invalidateUseStaffGetContractDates(campaignId, contractId)
    } catch {
      setSaveState(SAVE_BUTTON_STATES.ERROR)
      setIsEditing(false)
    }
  }

  const onSaveClick = async () => {
    if (!clause?.clauseId) return
    if (saveState !== SAVE_BUTTON_STATES.UNSAVED) return

    setValidateDueDate(true)

    if (!isDueDateValid) return

    setSaveState(SAVE_BUTTON_STATES.SAVING)

    try {
      const preview = await staffPreviewLinkedDates({
        campaignId,
        contractId,
        body: {
          sourceDateType:
            StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          proposedDate: calculatedDueDate.dueDateString || undefined,
          specialConditionClauseId: clause.clauseId,
        },
      })

      const impactedDates = preview?.impactedDates ?? []
      if (impactedDates.length > 0) {
        setLinkedDateImpacts(impactedDates)
        setSaveState(SAVE_BUTTON_STATES.UNSAVED)
        return
      }

      await saveDueDate()
    } catch {
      setSaveState(SAVE_BUTTON_STATES.ERROR)
    }
  }

  const onCancelLinkedDateWarning = () => {
    setLinkedDateImpacts([])
    setSaveState(SAVE_BUTTON_STATES.UNSAVED)
  }

  const onConfirmLinkedDateWarning = () => {
    setLinkedDateImpacts([])
    setSaveState(SAVE_BUTTON_STATES.SAVING)
    void saveDueDate()
  }

  return (
    <>
      <AgxSpacer fill direction="vertical" gap="md">
        <AgxTypography
          type="body_ui"
          size="small"
          id={`clause-${clause.clauseId}`}
          color="var(--text-secondary)"
        >
          Clause {clause.clausePosition}: {clause.clauseText}
        </AgxTypography>
        <AgxSpacer
          direction="horizontal"
          fill
          contentAlignment="end"
          gap="spacing-xl"
        >
          {isEditing ? (
            <AgxOnOrDaysFrom
              id={`clause-${clause.clauseId}-date`}
              condition={localDueDateCondition}
              conditionsToIncludeInOn={conditionsToInclude}
              conditionsToIncludeInDaysFrom={conditionsToInclude}
              specialConditions={specialConditions}
              campaignId={campaignId}
              onChange={setLocalDueDateCondition}
              validate={validateDueDate}
              contractDateType={ContractDateType.ContractDate}
              includeContractDateTypeInOnOptions={false}
              onIsValidChange={setIsDueDateValid}
            />
          ) : localDueDateCondition.condition ? (
            <ConditionDate
              id={`clause-${clause.clauseId}-date`}
              condition={localDueDateCondition}
              specialConditionIdToClauseNumberMap={
                specialConditionIdToClauseNumberMap
              }
            />
          ) : (
            <AgxSpacer fill direction="horizontal" contentAlignment="center">
              <AgxTypography
                id={`clause-${clause.clauseId}-date`}
                type="body_ui"
                size="small"
                color="var(--text-secondary)"
              >
                No Due Date
              </AgxTypography>
            </AgxSpacer>
          )}

          {isEditing ? (
            <AgxSaveChangesButton
              state={saveState}
              id={`clause-${clause.clauseId}-save-button`}
              text={saveButtonText}
              variant={'primary'}
              onClick={onSaveClick}
            />
          ) : (
            <AgxButton
              id={`clause-${clause.clauseId}-button`}
              text={'Edit'}
              variant={'hollow'}
              size="medium"
              onClick={() => setIsEditing(true)}
            />
          )}
        </AgxSpacer>
      </AgxSpacer>
      {!isLastClause && <AgxDivider variant={'horizontal'} />}
      {linkedDateImpacts.length > 0 && (
        <LinkedDateWarningModal
          impactedDates={linkedDateImpacts}
          sourceDateTypes={[
            StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          ]}
          onCancel={onCancelLinkedDateWarning}
          onConfirm={onConfirmLinkedDateWarning}
        />
      )}
    </>
  )
}

export default SpecialConditionClause
